import React from "react";
import { useFormik } from "formik";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Button, Input, MultiSelect } from "../../components/ui";
import CreateListModal from "../../components/Modals/CreateListModal";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { createList } from "../../store/features/ownerActions";
import { setListId, setLists } from "../../store/slices/ownerSlice";
import { RootState, ownerSelector } from "../../store/store";
import { checkExistingName, fetchLists, fetchProspects } from "../../utils";
import { DEFAULT_CAMPAIGN_NAME } from "./campaignFlow";
import {
  type CampaignFlowState,
  fetchCampaignDraft,
  draftSearchFromCampaignId,
  resolveFlowScope,
  resolveCampaignIdFromStateOrSearch,
  upsertCampaignDraft,
  MAX_CAMPAIGN_LIST_IDS,
  normalizeCampaignListIds,
} from "./campaignDraftFlow";

const campaignStepOneSchema = yup.object({
  campaignName: yup.string().trim().required("Campaign name is required"),
  leadsListIds: yup
    .array()
    .of(yup.string().required())
    .min(1, "Please select at least one list of leads")
    .max(MAX_CAMPAIGN_LIST_IDS, `You can link up to ${MAX_CAMPAIGN_LIST_IDS} lists`),
  excludeListId: yup.string().optional(),
  excludeContactedFromCampaign: yup.boolean().required(),
  excludeMessagedByOtherSenders: yup.boolean().required(),
  excludeContactedBySameSender: yup.boolean().required(),
});

const normalizeId = (value: any): string => {
  console.log("value", value);
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value._id === "string") return value._id;
    if (typeof value.$oid === "string") return value.$oid;
  }
  try {
    return String(value);
  } catch {
    return "";
  }
};

type StepOnePayload = {
  campaignName: string;
  leadsListIds: string[];
  leadsListId: string;
  excludeListId?: string;
  excludeRules: {
    excludeContactedFromOtherCampaign: boolean;
    excludeMessagedByOtherSenders: boolean;
    excludeContactedBySameSenderFromOtherCampaign: boolean;
  };
};

type StepOneFormValues = {
  campaignName: string;
  leadsListIds: string[];
  excludeListId: string;
  excludeContactedFromCampaign: boolean;
  excludeMessagedByOtherSenders: boolean;
  excludeContactedBySameSender: boolean;
};

const buildStepOnePayloadFromForm = (values: StepOneFormValues): StepOnePayload => ({
  campaignName: values.campaignName,
  leadsListIds: values.leadsListIds,
  leadsListId: values.leadsListIds[0] || "",
  excludeListId: values.excludeListId || undefined,
  excludeRules: {
    excludeContactedFromOtherCampaign: values.excludeContactedFromCampaign,
    excludeMessagedByOtherSenders: values.excludeMessagedByOtherSenders,
    excludeContactedBySameSenderFromOtherCampaign: values.excludeContactedBySameSender,
  },
});

export default function CampaignCreateLeadsStep() {
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { run: runContinue, isPending: isContinuing } = useAsyncAction();
  const { owner, currentWorkspace } = useSelector(ownerSelector);
  const flowState = (location.state as CampaignFlowState | null) || {};
  const campaignId = resolveCampaignIdFromStateOrSearch(flowState, searchParams.get("campaignId"));
  const preselectedListId = String(searchParams.get("listId") || "").trim();
  const [hydratedStepOnePayload, setHydratedStepOnePayload] = React.useState<
    CampaignFlowState["stepOnePayload"] | null
  >(
    flowState.stepOnePayload ||
      (preselectedListId
        ? {
            campaignName: DEFAULT_CAMPAIGN_NAME,
            leadsListIds: [preselectedListId],
            leadsListId: preselectedListId,
          }
        : null)
  );
  const [hydratedSelectedListLabel, setHydratedSelectedListLabel] = React.useState("");
  const stateCampaignName =
    hydratedStepOnePayload?.campaignName || flowState.campaignName || DEFAULT_CAMPAIGN_NAME;
  const [isCreateListModalOpen, setIsCreateListModalOpen] = React.useState(false);
  const [leadName, setLeadName] = React.useState(stateCampaignName);
  const [leadDescription, setLeadDescription] = React.useState("");
  const [listOptions, setListOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const selectedLeadsListIds = React.useMemo(
    () =>
      normalizeCampaignListIds(
        hydratedStepOnePayload?.leadsListIds ||
          (hydratedStepOnePayload?.leadsListId
            ? [hydratedStepOnePayload.leadsListId]
            : []),
        flowState.stepOnePayload?.leadsListId
      ),
    [
      flowState.stepOnePayload?.leadsListId,
      hydratedStepOnePayload?.leadsListId,
      hydratedStepOnePayload?.leadsListIds,
    ]
  );
  const existingDraftCampaignName = (hydratedStepOnePayload?.campaignName || "").trim();

  const resolveScope = React.useCallback(() => {
    return resolveFlowScope({
      flowState,
      owner,
      currentWorkspace,
    });
  }, [flowState, owner, currentWorkspace]);

  const fetchLeadListOptions = React.useCallback(async () => {
    const { workspaceId, userId, ownerId } = resolveScope();
    if (!ownerId) {
      setListOptions([]);
      return;
    }

    try {
      const lists = await fetchLists(ownerId, workspaceId, userId);
      const normalized = (lists || [])
        .filter((list: any) => {
          const listId = normalizeId(list?._id);
          if (list?.isDeleted) return false;
          if (list?.listName === "All Prospects") return false;
          if (list?.inActiveCampaign && !selectedLeadsListIds.includes(listId)) return false;
          // Match previous flow: only show lists that already contain prospects.
          const prospectCount = Array.isArray(list?.prospects)
            ? list.prospects.length
            : Number(list?.nProspect || 0);
          if (selectedLeadsListIds.includes(listId)) return true;
          return prospectCount > 0;
        })
        .map((list: any) => ({ value: normalizeId(list._id), label: list.listName }));
      const missingSelected = selectedLeadsListIds.filter(
        (id) => !normalized.some((option: { value: string; label: string }) => option.value === id)
      );
      for (const missingId of missingSelected) {
        normalized.unshift({
          value: missingId,
          label: hydratedSelectedListLabel || "Previously selected list",
        });
      }
      setListOptions(normalized);
    } catch {
      setListOptions([]);
    }
  }, [hydratedSelectedListLabel, resolveScope, selectedLeadsListIds]);

  React.useEffect(() => {
    fetchLeadListOptions();
  }, [fetchLeadListOptions]);

  React.useEffect(() => {
     console.log("campaignId", campaignId);

    const hydrateDraft = async () => {
      if (!campaignId || hydratedStepOnePayload) return;
      const { workspaceId, userId, ownerId } = resolveScope();
      console.log("campaignId", campaignId);
      console.log("workspaceId", workspaceId);
      console.log("userId", userId);
      console.log("ownerId", ownerId);
      try {
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId,
          userId,
          workspaceId,
        });
        const campaign = draftResponse?.campaign;
        if (!campaign || typeof campaign !== "object") return;
        const hydratedListIds = normalizeCampaignListIds(campaign?.listIds, campaign?.listId);
        const hydrated = {
          campaignName: campaign?.campaignName || "",
          leadsListIds: hydratedListIds,
          leadsListId: hydratedListIds[0] || "",
          excludeListId: normalizeId(campaign?.excludeListId),
          excludeRules: {
            excludeContactedFromOtherCampaign: Boolean(
              campaign?.excludeRules?.excludeContactedFromOtherCampaign
            ),
            excludeMessagedByOtherSenders: Boolean(
              campaign?.excludeRules?.excludeMessagedByOtherSenders
            ),
            excludeContactedBySameSenderFromOtherCampaign: Boolean(
              campaign?.excludeRules?.excludeContactedBySameSenderFromOtherCampaign
            ),
          },
        };
        setHydratedStepOnePayload(hydrated);
        const selectedListLabel =
          Array.isArray(campaign?.listIds) && campaign.listIds.length > 0
            ? campaign.listIds
                .map((list: any) =>
                  typeof list === "object" ? String(list?.listName || "") : ""
                )
                .filter(Boolean)
                .join(", ")
            : campaign?.listId && typeof campaign.listId === "object"
              ? String(campaign.listId?.listName || "")
              : "";
        setHydratedSelectedListLabel(selectedListLabel);
      } catch (error) {
        console.log(error);
      }
    };
    void hydrateDraft();
  }, [campaignId, hydratedStepOnePayload, resolveScope]);

  React.useEffect(() => {
    if (!isCreateListModalOpen) return;
    setLeadName(formik.values.campaignName || stateCampaignName);
    setLeadDescription("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateListModalOpen, stateCampaignName]);

  const excludeListOptions = React.useMemo(
    () => [{ value: "", label: "None" }, ...listOptions],
    [listOptions]
  );

  const handleCreateLead = async () => {
    const { workspaceId, userId, ownerId } = resolveScope();
    if (!leadName.trim()) {
      toast.error("Lead name is required");
      return;
    }
    if (!workspaceId) {
      toast.error("Missing workspace context");
      return;
    }
    if (!ownerId) {
      toast.error("Unable to resolve owner scope");
      return;
    }

    try {
      const result = await dispatch(
        createList({
          ownerId,
          userId,
          workspaceId,
          listName: leadName.trim(),
          description: leadDescription.trim(),
        })
      );

      if ((result as any)?.meta?.requestStatus !== "fulfilled") {
        toast.error("Unable to create lead");
        return;
      }

      const createdLeadId = (result as any)?.payload?._id;
      const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
      const mergedLists =
        createdLeadId &&
        !refreshedLists.some((l: any) => l._id === createdLeadId) &&
        (result as any)?.payload
          ? [...refreshedLists, (result as any).payload]
          : refreshedLists;

      dispatch(setLists(mergedLists));
      const createdLead =
        mergedLists.find((l: any) => l._id === createdLeadId) ||
        mergedLists.find((l: any) => l.listName === leadName.trim());

      if (!createdLead?._id) {
        toast.error("Lead created, but unable to open it automatically.");
        return;
      }

      dispatch(setListId(createdLead._id));
      setIsCreateListModalOpen(false);
      toast.success("Lead created successfully");
      navigate(`/prospects/${createdLead._id}`);
    } catch (error) {
      console.log(error);
      toast.error("An error occurred");
    }
  };

  const formik = useFormik<StepOneFormValues>({
    initialValues: {
      campaignName: stateCampaignName,
      leadsListIds: selectedLeadsListIds,
      excludeListId: hydratedStepOnePayload?.excludeListId || "",
      excludeContactedFromCampaign:
        Boolean(hydratedStepOnePayload?.excludeRules?.excludeContactedFromOtherCampaign) || false,
      excludeMessagedByOtherSenders:
        Boolean(hydratedStepOnePayload?.excludeRules?.excludeMessagedByOtherSenders) || false,
      excludeContactedBySameSender:
        Boolean(hydratedStepOnePayload?.excludeRules?.excludeContactedBySameSenderFromOtherCampaign) || false,
    },
    validationSchema: campaignStepOneSchema,
    onSubmit: async () => {},
    enableReinitialize: true,
  });

  React.useEffect(() => {
    if (!hydratedStepOnePayload) return;
    formik.setValues({
      campaignName: hydratedStepOnePayload.campaignName || DEFAULT_CAMPAIGN_NAME,
      leadsListIds: normalizeCampaignListIds(
        hydratedStepOnePayload.leadsListIds,
        hydratedStepOnePayload.leadsListId
      ),
      excludeListId: normalizeId(hydratedStepOnePayload.excludeListId),
      excludeContactedFromCampaign: Boolean(
        hydratedStepOnePayload?.excludeRules?.excludeContactedFromOtherCampaign
      ),
      excludeMessagedByOtherSenders: Boolean(
        hydratedStepOnePayload?.excludeRules?.excludeMessagedByOtherSenders
      ),
      excludeContactedBySameSender: Boolean(
        hydratedStepOnePayload?.excludeRules?.excludeContactedBySameSenderFromOtherCampaign
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedStepOnePayload]);

  const handleContinue = () =>
    runContinue(async () => {
    const errors = await formik.validateForm();
    formik.setTouched(
      {
        campaignName: true,
        leadsListIds: true,
        excludeListId: true,
        excludeContactedFromCampaign: true,
        excludeMessagedByOtherSenders: true,
        excludeContactedBySameSender: true,
      },
      true
    );

    if (errors?.leadsListIds) {
      toast.error("Please select at least one list of leads.");
      return;
    }

    if (errors?.campaignName) {
      toast.error("Campaign name is required.");
      return;
    }

    const { workspaceId, userId, ownerId } = resolveScope();
    if (!ownerId) {
      toast.error("Unable to resolve owner scope");
      return;
    }
    const nextCampaignName = formik.values.campaignName.trim();
    const isSameDraftName =
      Boolean(campaignId) &&
      existingDraftCampaignName.length > 0 &&
      existingDraftCampaignName.toLowerCase() === nextCampaignName.toLowerCase();
    if (!isSameDraftName) {
      const isNameAvailable = await checkExistingName(
        nextCampaignName,
        ownerId,
        userId,
        workspaceId
      );
      if (!isNameAvailable) {
        toast.error("Campaign name already exists");
        return;
      }
    }

    const stepOnePayload = buildStepOnePayloadFromForm({
      ...formik.values,
      campaignName: nextCampaignName,
    });
    let prospectIds: string[] = [];
    try {
      const listResults = await Promise.all(
        stepOnePayload.leadsListIds.map((listId) =>
          fetchProspects(ownerId, listId, 1, 3000, userId, workspaceId)
        )
      );
      const seenProspectIds = new Set<string>();
      for (const listData of listResults) {
        const ids = Array.isArray(listData?.prospects)
          ? listData.prospects
              .map((prospect: any) => prospect?._id)
              .filter((prospectId: any) => typeof prospectId === "string" && prospectId)
          : [];
        for (const prospectId of ids) {
          if (seenProspectIds.has(prospectId)) continue;
          seenProspectIds.add(prospectId);
          prospectIds.push(prospectId);
        }
      }
    } catch {
      toast.error("Unable to fetch prospects for selected lists.");
      return;
    }

    if (prospectIds.length === 0) {
      toast.error("Selected lists have no prospects.");
      return;
    }

    const loader = toast.loading("Saving changes...");
    try {
      const draftResponse = await upsertCampaignDraft({
        campaignId,
        ownerId,
        userId,
        workspaceId,
        campaignName: stepOnePayload.campaignName,
        listId: stepOnePayload.leadsListIds[0],
        listIds: stepOnePayload.leadsListIds,
        prospects: prospectIds,
        excludeListId: stepOnePayload.excludeListId,
        excludeRules: stepOnePayload.excludeRules,
      });
      const nextCampaignId = draftResponse?.campaignId || campaignId;
      toast.update(loader, {
        render: "Saved successfully.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });

      navigate(
        {
          pathname: "senders",
          search: draftSearchFromCampaignId(nextCampaignId),
        },
        {
          state: {
            ...flowState,
            campaignId: nextCampaignId,
            campaignName: stepOnePayload.campaignName,
            scope: { ownerId, userId, workspaceId },
            stepOnePayload,
          },
        },
      );
    } catch (error) {
      toast.update(loader, {
        render: "Unable to save changes.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      return;
    }
  });

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 gap-6 px-4 py-5 sm:px-6 md:grid-cols-2">
          <div className="space-y-6">
            <Input
              label="Campaign name *"
              name="campaignName"
              value={formik.values.campaignName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.campaignName
                  ? typeof formik.errors.campaignName === "string"
                    ? formik.errors.campaignName
                    : ""
                  : ""
              }
              size="sm"
            />

         
          </div>

          <div className="space-y-6">
            <MultiSelect
              label="Select the lists of leads you want to reach out to *"
              placeholder="Select lists"
              options={listOptions}
              value={formik.values.leadsListIds}
              maxSelections={MAX_CAMPAIGN_LIST_IDS}
              onMaxSelectionsReached={() =>
                toast.error(`You can link up to ${MAX_CAMPAIGN_LIST_IDS} lists.`)
              }
              onChange={(values) => {
                formik.setFieldValue("leadsListIds", values);
                formik.setFieldTouched("leadsListIds", true, false);
              }}
              error={
                formik.touched.leadsListIds &&
                typeof formik.errors.leadsListIds === "string"
                  ? formik.errors.leadsListIds
                  : undefined
              }
            />

            <Button
              type="button"
              variant="outline"
              size="sm"
              fullWidth
              className="text-dark-grey"
              iconSrc="/icons/empty_list.svg"
              showBefore
              onClick={() => setIsCreateListModalOpen(true)}
            >
              Create empty list
            </Button>
          </div>

          {/* <Select
            label="Select exclude list"
            placeholder="Select"
            value={formik.values.excludeListId}
            onChange={(value) => formik.setFieldValue("excludeListId", value)}
            options={excludeListOptions}
            size="md"
          /> */}

          {/* <div className="space-y-2">
            <p className="text-md font-medium text-dark-black">Exclude Options</p>
            <div className="rounded-[8px] bg-bg-blue px-4 py-3">
              <div className="space-y-2">
                <Checkbox
                  label="Exclude leads contacted from other heyreach campaign"
                  checked={formik.values.excludeContactedFromCampaign}
                  onChange={(checked) => formik.setFieldValue("excludeContactedFromCampaign", checked)}
                />
                <Checkbox
                  label="Exclude leads messaged by other senders"
                  checked={formik.values.excludeMessagedByOtherSenders}
                  onChange={(checked) => formik.setFieldValue("excludeMessagedByOtherSenders", checked)}
                />
                <Checkbox
                  label="Exclude leads contacted by same sender from other heyreach campaign"
                  checked={formik.values.excludeContactedBySameSender}
                  onChange={(checked) => formik.setFieldValue("excludeContactedBySameSender", checked)}
                />
              </div>
            </div>
          </div> */}
        </div>
        </div>

        <div className="shrink-0 border-t border-light-grey bg-white px-4 py-4 sm:px-6">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="min-w-[120px]"
              iconSrc="/icons/arrow_right.svg"
              showAfter
              isLoading={isContinuing}
              onClick={handleContinue}
            >
              {isContinuing ? "Saving..." : "Continue"}
            </Button>
          </div>
        </div>
      </form>

      <CreateListModal
        isOpen={isCreateListModalOpen}
        onClose={() => setIsCreateListModalOpen(false)}
        onSave={handleCreateLead}
        listName={""}
        onListNameChange={setLeadName}
        description={leadDescription}
        onDescriptionChange={setLeadDescription}
      />
    </>
  );
}
